'use client';

import type { UpdatingDataCriteria, UpdatingDataValue } from '~/components/Profile/model';
import type { CriteriaChoiceUserType } from '~/model/CriteriaChoiceType';
import type { CriteriaType } from '~/model/CriteriaType';
import { VtmnTextInput } from '@vtmn/react';
import format from 'date-fns-tz/format';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import { retrieveTranslatedLabel } from '~/components/Interaction/Form/utils';
import { AcceptLanguage } from '~/const/locale';

export const CriteriaDate = ({
  criteria,
  userCriteria,
  updatingUserValue,
  handleChange,
  required,
  helperText,
  noLabel = false,
}: {
  criteria: CriteriaType;
  userCriteria: CriteriaChoiceUserType;
  updatingUserValue: UpdatingDataValue;
  handleChange: (obj: UpdatingDataCriteria) => void;
  sportId?: number;
  required?: boolean;
  helperText?: string;
  noLabel?: boolean;
}) => {
  const locale = useLocale();
  const t = useTranslations('PROFILE');

  const [isErrorDisplayed, setIsErrorDisplayed] = useState(false);
  const [hasBeenFocused, setHasBeenFocused] = useState(false);

  const type = criteria.type;
  const criteriaUri = criteria['@id'];
  const valueDate = userCriteria?.value_date;
  const defaultDateValue
    = valueDate && format(new Date(valueDate), 'yyyy-MM-dd');
  const hasBeenCleared = updatingUserValue === null;
  const updatingValue
    = updatingUserValue?.value_date
      && format(new Date(updatingUserValue.value_date), 'yyyy-MM-dd');

  const value = useMemo(() => {
    if (hasBeenCleared)
      return null;
    else return updatingValue || defaultDateValue;
  }, [defaultDateValue, hasBeenCleared, updatingValue]);

  const inputValue = value || '';
  const isOnError = required && !inputValue;
  const placeHolder = !inputValue ? t('GLOBAL.PLACEHOLDER_DATE') : '';
  const errorText = isOnError ? helperText : placeHolder;

  const handleChangeValue = (date: string | null) => {
    handleChange({
      value: date
        ? { value_date: format(new Date(date), 'yyyy-MM-dd') }
        : null,
      criteriaUri,
      type,
      ignore: !userCriteria && date === null,
    });
  };

  useEffect(() => {
    if (hasBeenFocused) {
      if (isOnError && !isErrorDisplayed)
        setIsErrorDisplayed(true);
      else if (!isOnError && isErrorDisplayed)
        setIsErrorDisplayed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnError, hasBeenFocused]);

  return (
    <div className="profile__field" data-testid="criteria-date">
      <VtmnTextInput
        identifier="criteria-date"
        value={inputValue}
        labelText={
          noLabel
            ? null
            : retrieveTranslatedLabel(
                criteria,
                AcceptLanguage[locale],
              )
        }
        onChange={event => handleChangeValue(event.target.value)}
        aria-label={t('GLOBAL.PLACEHOLDER_DATE')}
        required={required}
        type="date"
        error={isErrorDisplayed}
        {...(isErrorDisplayed && { helperText: errorText })}
        data-cy="criteriaDate"
        {...inputValue && { icon: 'close-line' }}
        onIconClick={() => handleChangeValue(null)}
        onBlur={() => setHasBeenFocused(true)}
      />
    </div>
  );
};
